import { useEffect, useState } from 'react'
import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { ScrollArea } from '@journ/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@journ/ui/select'
import { Sheet, SheetContent, SheetHeader } from '@journ/ui/sheet'
import { SubmitButton } from '@journ/ui/submit-button'
import { useIntlayer } from 'react-intlayer'
import { useSelector } from '@/libs/state'
import { updatePhase } from '../application/manage-phase'
import { activeWorkflow$, editingPhaseId$, teams$ } from '../store'
import { workflows as workflowsPort, notify } from '../wiring'

type Props = {
	onSuccess: () => void
}

const PhaseEditSheet = ({ onSuccess }: Props) => {
	const phaseId = useSelector(editingPhaseId$)
	const workflow = useSelector(activeWorkflow$)
	const teams = useSelector(teams$)
	const content = useIntlayer('workflow-editor').editPhase

	const phase = workflow?.phases.find(p => p.id === phaseId) ?? null
	const isOpen = phase !== null

	const [name, setName] = useState('')
	const [teamId, setTeamId] = useState('')
	const [submitting, setSubmitting] = useState(false)

	useEffect(() => {
		if (phase) {
			setName(phase.name)
			setTeamId(phase.teamId)
		}
	}, [phase])

	const close = () => {
		editingPhaseId$.set(null)
		setName('')
		setTeamId('')
	}

	const submit = async () => {
		if (!phase || !name.trim() || !teamId) return

		setSubmitting(true)
		const result = await updatePhase(workflowsPort, { id: phase.id, name: name.trim(), teamId }, notify)
		setSubmitting(false)

		if (result) {
			onSuccess()
			close()
		}
	}

	const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		await submit()
	}

	return (
		<Sheet open={isOpen} onOpenChange={close}>
			<SheetContent stack title={content.title.value}>
				<SheetHeader className='mb-6 flex justify-between items-center flex-row'>
					<h2 className='text-xl'>{content.title}</h2>
					<Button size='icon' variant='ghost' onClick={close} className='p-0 m-0 size-auto hover:bg-transparent'>
						<Icons.Close className='size-5' />
					</Button>
				</SheetHeader>

				<ScrollArea className='h-full p-0 pb-28' hideScrollbar>
					<form onSubmit={handleSubmit} className='flex flex-col gap-5'>
						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{content.nameLabel}</Label>
							<Input
								value={name}
								onChange={e => setName(e.target.value)}
								placeholder={content.namePlaceholder.value}
								autoFocus
								autoComplete='off'
							/>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{content.teamLabel}</Label>
							<Select value={teamId} onValueChange={setTeamId}>
								<SelectTrigger>
									<SelectValue placeholder={content.teamPlaceholder.value} />
								</SelectTrigger>
								<SelectContent>
									{teams.map(team => (
										<SelectItem key={team.id} value={team.id}>
											{team.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</form>
				</ScrollArea>

				<div className='absolute bottom-0 left-0 right-0 p-4 bg-background border-t border-border'>
					<div className='flex justify-end space-x-4'>
						<Button variant='outline' type='button' onClick={close}>
							{content.cancel}
						</Button>
						<SubmitButton isSubmitting={submitting} disabled={!name.trim() || !teamId} onClick={submit}>
							{content.save}
						</SubmitButton>
					</div>
				</div>
			</SheetContent>
		</Sheet>
	)
}

export { PhaseEditSheet }
